import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import type {
  ServicePricing,
  ServicePricingAddon,
  ServicePricingPackage,
  ServicePricingTier,
} from '@/lib/cms-types';

const fmtGHS = (n: number) =>
  `GH₵ ${n.toLocaleString('en-GH', { maximumFractionDigits: 0 })}`;
const fmtUSD = (n: number) =>
  `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

interface Props {
  pricing: ServicePricing;
}

/**
 * Renders the tiered-pricing card for services that have `pricing` set
 * (e.g. Short AI Video Content). Self-contained — `ServicesPage` just
 * drops it inside `ServiceSection` after the standard deliverables block.
 */
export function VideoPricingBlock({ pricing }: Props) {
  return (
    <div className="mt-8 space-y-10 sm:mt-10">
      {pricing.lede && (
        <p className="max-w-3xl text-sm leading-relaxed text-fg-muted sm:text-base">
          {pricing.lede}
        </p>
      )}

      {pricing.categories && pricing.categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {pricing.categories.map((c) => (
            <Badge key={c} variant="outline">{c}</Badge>
          ))}
        </div>
      )}

      {/* Single-video tiers */}
      {pricing.tiers.length > 0 && (
        <div>
          <SectionHeading eyebrow="Single video" title="Pick a tier" />
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pricing.tiers.map((tier) => (
              <TierCard key={tier.name} tier={tier} />
            ))}
          </div>
        </div>
      )}

      {/* Monthly packages */}
      {pricing.monthly.length > 0 && (
        <div>
          <SectionHeading
            eyebrow="Monthly packages"
            title="Save 15 – 20% vs single video"
            sub="Best for brands shipping every week."
          />
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pricing.monthly.map((pkg) => (
              <PackageCard key={pkg.name} pkg={pkg} />
            ))}
          </div>
        </div>
      )}

      {/* Add-ons */}
      {pricing.addons.length > 0 && (
        <div>
          <SectionHeading eyebrow="Add-ons" title="Make it yours" />
          <ul className="mt-5 divide-y divide-border overflow-hidden rounded-lg border border-border bg-bg-elevated/30">
            {pricing.addons.map((a) => (
              <AddonRow key={a.name} addon={a} />
            ))}
          </ul>
        </div>
      )}

      {/* Rules / terms */}
      {hasRules(pricing) && (
        <div className="rounded-xl border border-border bg-bg-elevated/40 p-5 sm:p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">
            Terms & logistics
          </p>
          <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-2">
            {pricing.rules.footage && (
              <Rule term="Footage" body={pricing.rules.footage} />
            )}
            {pricing.rules.revisions && (
              <Rule term="Revisions" body={pricing.rules.revisions} />
            )}
            {pricing.rules.payment && (
              <Rule term="Payment" body={pricing.rules.payment} />
            )}
            {pricing.rules.delivery && (
              <Rule term="Delivery" body={pricing.rules.delivery} />
            )}
          </dl>
        </div>
      )}

      {/* Bundle callout */}
      {pricing.bundle && (
        <div className="rounded-2xl border border-gold-500/30 bg-gold-500/5 p-5 sm:p-8">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-gold-500" />
            <div className="flex-1">
              <h4 className="display-serif text-xl text-fg sm:text-2xl">
                {pricing.bundle.headline}
              </h4>
              <p className="mt-2 text-sm text-fg-muted sm:text-base">
                {pricing.bundle.body}
              </p>
            </div>
          </div>
          {pricing.bundle.ctaHref && (
            <div className="mt-5">
              <Button asChild variant="secondary">
                <Link to={pricing.bundle.ctaHref}>
                  {pricing.bundle.ctaLabel ?? 'Learn more'} <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionHeading({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: string;
  sub?: string;
}) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">
        {eyebrow}
      </p>
      <h3 className="mt-1 text-h3 text-fg">{title}</h3>
      {sub && <p className="mt-1 text-sm text-fg-muted">{sub}</p>}
    </div>
  );
}

function TierCard({ tier }: { tier: ServicePricingTier }) {
  return (
    <div
      className={[
        'relative flex h-full flex-col rounded-xl border bg-bg-elevated/40 p-5 sm:p-6',
        tier.popular ? 'border-gold-500/60 ring-1 ring-gold-500/30' : 'border-border',
      ].join(' ')}
    >
      {tier.popular && (
        <span className="absolute -top-3 left-5 inline-flex items-center rounded-full bg-gold-500 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-bg">
          Most popular
        </span>
      )}
      <h4 className="display-serif text-xl text-fg">{tier.name}</h4>
      <p className="mt-1 text-xs uppercase tracking-wider text-fg-subtle">{tier.length}</p>
      <p className="mt-3 text-sm text-fg-muted">{tier.bestFor}</p>

      <div className="mt-5 border-t border-border pt-5">
        <div className="flex items-baseline gap-2">
          <span className="display-serif text-2xl text-fg">{fmtGHS(tier.priceGHS)}</span>
          <span className="text-sm text-fg-subtle">/ {fmtUSD(tier.priceUSD)}</span>
        </div>
        <p className="mt-0.5 text-xs text-fg-subtle">per video</p>
      </div>

      <ul className="mt-5 flex-1 space-y-2">
        {tier.includes.map((line) => (
          <li key={line} className="flex items-start gap-2 text-sm text-fg">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" />
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PackageCard({ pkg }: { pkg: ServicePricingPackage }) {
  return (
    <div
      className={[
        'relative flex h-full flex-col rounded-xl border bg-bg-elevated/40 p-5 sm:p-6',
        pkg.popular ? 'border-gold-500/60 ring-1 ring-gold-500/30' : 'border-border',
      ].join(' ')}
    >
      {pkg.popular && (
        <span className="absolute -top-3 left-5 inline-flex items-center rounded-full bg-gold-500 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-bg">
          Most popular
        </span>
      )}
      <h4 className="display-serif text-xl text-fg">{pkg.name}</h4>
      <p className="mt-1 text-xs uppercase tracking-wider text-fg-subtle">
        {pkg.videosPerMonth}
      </p>
      <p className="mt-3 text-sm text-fg-muted">{pkg.bestFor}</p>

      <div className="mt-5 border-t border-border pt-5">
        <div className="flex items-baseline gap-2">
          <span className="display-serif text-2xl text-fg">{fmtGHS(pkg.priceGHS)}</span>
          <span className="text-sm text-fg-subtle">/ {fmtUSD(pkg.priceUSD)}</span>
        </div>
        <p className="mt-0.5 text-xs text-fg-subtle">per month</p>
      </div>
    </div>
  );
}

function AddonRow({ addon }: { addon: ServicePricingAddon }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 sm:px-5">
      <span className="text-sm text-fg">{addon.name}</span>
      <span className="text-sm font-medium text-gold-500">{addon.price}</span>
    </li>
  );
}

function Rule({ term, body }: { term: string; body: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">
        {term}
      </dt>
      <dd className="mt-1 text-fg-muted">{body}</dd>
    </div>
  );
}

function hasRules(pricing: ServicePricing) {
  return Boolean(
    pricing.rules.footage ||
      pricing.rules.revisions ||
      pricing.rules.payment ||
      pricing.rules.delivery,
  );
}